/**
 * Detalhe do comprovante no Portal do Parceiro (Fatia E).
 *
 * Mostra SÓ dados do próprio parceiro: imagem + valor enviado + o que o OCR leu
 * (valor/data-hora) + status amigável + linha do tempo. O backend já garante
 * que nenhum dado interno (valor do boletim, motivo do operador) vem no payload.
 */
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import portalApi from "@/services/portalApi";

interface TimelineItem {
  label: string;
  at: string | null;
}

interface PortalComprovanteDetail {
  id: string;
  valor: number;
  status_amigavel?: string;
  categoria?: "info" | "success" | "warning" | "danger";
  created_at: string;
  ges_ponto_codigo: string;
  ges_ponto_nome: string;
  comprovante_data_hora?: string | null;
  ocr_valor?: number | null;
  imagem_url?: string | null;
  timeline?: TimelineItem[];
}

const CATEGORIA_VARIANT: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  success: "default",
  info: "secondary",
  warning: "outline",
  danger: "destructive",
};

const formatBRL = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatDateTime = (iso: string | null): string => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

interface Props {
  comprovanteId: string | null;
  onClose: () => void;
}

export function PortalComprovanteDetailDialog({ comprovanteId, onClose }: Props) {
  const open = !!comprovanteId;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["portal", "comprovante", comprovanteId],
    queryFn: async () => {
      const { data } = await portalApi.get(
        `/portal/comprovantes/${comprovanteId}`
      );
      return data as PortalComprovanteDetail;
    },
    enabled: open,
    retry: false,
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalhe do comprovante</DialogTitle>
          <DialogDescription>
            Acompanhe o status do seu comprovante e o que o sistema leu dele.
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Carregando…
          </div>
        )}

        {isError && (
          <p className="py-8 text-center text-sm text-red-600">
            Não foi possível carregar o comprovante.
          </p>
        )}

        {data && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono text-sm font-medium">
                  {data.ges_ponto_codigo}
                </div>
                <div className="text-xs text-muted-foreground">
                  {data.ges_ponto_nome}
                </div>
              </div>
              {data.status_amigavel && (
                <Badge variant={CATEGORIA_VARIANT[data.categoria || "info"] || "secondary"}>
                  {data.status_amigavel}
                </Badge>
              )}
            </div>

            {data.imagem_url && (
              <img
                src={data.imagem_url}
                alt="Comprovante"
                className="w-full rounded-md border"
              />
            )}

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-muted-foreground text-xs">Valor enviado</div>
                <div className="font-semibold">{formatBRL(data.valor)}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Valor lido (OCR)</div>
                <div className="font-mono">
                  {data.ocr_valor != null ? formatBRL(data.ocr_valor) : "—"}
                </div>
              </div>
              <div className="col-span-2">
                <div className="text-muted-foreground text-xs">
                  Data/hora do depósito (lida do comprovante)
                </div>
                <div className="font-mono">{data.comprovante_data_hora || "—"}</div>
              </div>
            </div>

            {data.timeline && data.timeline.length > 0 && (
              <div className="border-t pt-3">
                <div className="text-xs font-semibold text-muted-foreground mb-2">
                  Andamento
                </div>
                <ol className="space-y-2">
                  {data.timeline.map((t, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="mt-1 h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
                      <span>
                        <span className="font-medium">{t.label}</span>
                        {t.at && (
                          <span className="text-muted-foreground">
                            {" "}
                            · {formatDateTime(t.at)}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
